import React from "react";

import { css, cx } from "@emotion/css";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";

import { useStyles } from "@/hooks/useStyles.ts";
import { userFormattedName } from "@/models/UserModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { AtomProps } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

export type BaseProfilePictureProps = {
  src: string | undefined;
  firstName: string;
  lastName: string;
  size?: "small" | "medium" | "large" | "scale";
  colorVariant?: "primary" | "secondary"; // Primary has blue background, secondary has red background (use secondary when blue on blue)
  tooltip?: string;
  tooltipClassName?: string;
  tooltipWrapperClassName?: string;
};

export type ProfilePictureProps = AtomProps<BaseProfilePictureProps>;

export const ProfilePicture: React.FC<ProfilePictureProps> = ({
  id,
  src,
  firstName,
  lastName,
  size = "medium",
  colorVariant = "primary",
  tooltip = false,
  className,
  tooltipClassName,
  tooltipWrapperClassName,
  role,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  const component = (
    <Avatar
      id={id}
      alt={userFormattedName(firstName, lastName)}
      src={src}
      className={cx(styles.root, styles[size], styles.variants[colorVariant], className)}
      {...props}
    >
      {userFormattedName(firstName, lastName)
        .split(" ")
        .map(word => word[0])
        .slice(0, 2)
        .join("")}
    </Avatar>
  );
  if (tooltip) {
    return (
      <Tooltip
        popupId={suffixId(id, "tooltip")}
        placement="bottom"
        className={tooltipClassName}
        wrapperClassName={tooltipWrapperClassName}
        hover={{ handleClose: null }}
        content={<Typography variant="body">{tooltip}</Typography>}
      >
        {component}
      </Tooltip>
    );
  }

  return component;
};

const makeStyles = (theme: Theme) => ({
  root: css({
    color: theme.palette.common.black,
  }),
  variants: {
    primary: css({
      backgroundColor: theme.palette.primary.background,
    }),
    secondary: css({
      backgroundColor: theme.palette.red.background,
    }),
  },
  small: css({
    width: theme.spacing(16),
    height: theme.spacing(16),
    fontSize: theme.typography.bodySmall.fontSize,
    fontWeight: "bold",
  }),
  medium: css({
    width: theme.spacing(20),
    height: theme.spacing(20),
    fontSize: theme.typography.body.fontSize,
    fontWeight: "bold",
  }),
  large: css({
    width: theme.spacing(24),
    height: theme.spacing(24),
    fontSize: theme.typography.bodyLarge.fontSize,
    fontWeight: "bold",
  }),
  scale: css({
    flex: "1 1 0",
    aspectRatio: "1",
    width: "100%",
    height: "100%",
    fontSize: "80%",
  }),
});
